import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { FilterSelectOrClear } from '@calendly/ui/components/dropdown';
import { InlineSearch } from '@calendly/ui/components/inline-search';
import { PopupDropdownMenu } from '@calendly/ui/components/menu';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { uniqBy } from 'lodash-es';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Layout } from 'components/layout';
import { useDebounce } from 'hooks/useDebounce';
import { isEnterKey, isEscapeKey } from 'services/keycodes';
import { SearchContent } from './search_content';
import { useSearchEventTypeHostsQuery } from '../../../../../../api';
import { eventTypeMembershipToSuggestion, suggestionToEventTypeMembership } from '../../helpers';
const Controls = props => <Layout paddingX="16" paddingY="8" gap="16" direction="column" {...props} />;
const MenuContent = forwardRef((props, ref) => <Layout direction="column" ref={ref} {...props} />);
const styledPopupDropdownMenuIsMultiSelectCss = "s1ps0do0";
const styledPopupDropdownMenuIsPhoneCss = "s1kv1r46";
const styledPopupDropdownMenuIsAddingRotatingHostsCss = "s1nuxjwi";
const styledPopupDropdownMenuCss = "s11p3258";
const StyledPopupDropdownMenu = forwardRef((props, ref) => {
  const {
    isMultiSelect,
    isAddingRotatingHosts
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <PopupDropdownMenu ref={ref} {...props} className={classNames(styledPopupDropdownMenuCss, {
    [styledPopupDropdownMenuIsMultiSelectCss]: isMultiSelect,
    [styledPopupDropdownMenuIsPhoneCss]: isPhone,
    [styledPopupDropdownMenuIsAddingRotatingHostsCss]: isAddingRotatingHosts
  }, props.className)} />;
});
const filterContainerCss = "fqsw3iv";
const FilterContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(filterContainerCss, props.className)} />;
});
const footerIsPhoneCss = "fzy8u5x";
const footerCss = "fez3tj4";
const Footer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(footerCss, {
    [footerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const filterSuggestionByNameOrEmail = (hosts, query) => hosts.filter(({
  entity: {
    name,
    email
  }
}) => {
  const loweredQuery = query.toLowerCase();
  return name?.toLowerCase()?.includes(loweredQuery) || email.toLowerCase().includes(loweredQuery);
});
export const HostPicker = ({
  isAddingRotatingHosts = false,
  isDisabled = false,
  isMultiSelect = false,
  memberships = [],
  onClose,
  onSelect,
  placeholder,
  prepareHostOptionProps,
  searchForGroupAdmin = false,
  searchMembershipsOnly = false,
  skipSearch = false,
  ...rest // Part of the dark magic 🧙to prevent dropdown from closing
}) => {
  const isPhone = useDeviceType('isPhone');
  const menuRef = useRef();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const {
    data: {
      suggestions
    } = {},
    isFetching
  } = useSearchEventTypeHostsQuery({
    query: debouncedQuery,
    searchMembershipsOnly,
    searchForGroupAdmin
  }, {
    skip: skipSearch
  });
  const [allSuggestions, setAllSuggestions] = useState(uniqBy([...memberships.map(membership => ({
    ...eventTypeMembershipToSuggestion(membership),
    ...prepareHostOptionProps(membership)
  })), ...(suggestions || []).map(suggestion => ({
    ...suggestion,
    ...prepareHostOptionProps(suggestionToEventTypeMembership(suggestion))
  }))], 'id'));
  const [currentSuggestions, setCurrentSuggestions] = useState(skipSearch ? allSuggestions : suggestions);
  useOnValueChange(debouncedQuery, () => {
    if (!skipSearch) return;
    setCurrentSuggestions(debouncedQuery !== '' ? filterSuggestionByNameOrEmail(allSuggestions, debouncedQuery) : allSuggestions);
  });
  useOnValueChange(suggestions, () => {
    setCurrentSuggestions(suggestions);
    if (!suggestions) {
      return;
    }
    setAllSuggestions(uniqBy([...allSuggestions, ...suggestions.map(suggestion => ({
      ...suggestion,
      ...prepareHostOptionProps(suggestionToEventTypeMembership(suggestion))
    }))], 'id'));
  });
  useEffect(() => {
    if (isFetching || !currentSuggestions || allSuggestions.length === 0) return;
    if (menuRef.current) {
      menuRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [allSuggestions.length, currentSuggestions, isFetching]);
  const handleSingleSelection = suggestion => {
    setSearchQuery('');
    onSelect(suggestionToEventTypeMembership(suggestion));
  };
  const handleMultiSelection = useCallback(selectedSuggestion => {
    setAllSuggestions(allSuggestions.map(suggestion => selectedSuggestion.id === suggestion.id && !suggestion.disabled ? {
      ...suggestion,
      selected: !suggestion.selected
    } : suggestion));
  }, [allSuggestions]);
  const handleChangeAll = selected => setAllSuggestions(allSuggestions.map(suggestion => currentSuggestions.some(({
    id
  }) => id === suggestion.id) && !suggestion.disabled ? {
    ...suggestion,
    selected
  } : suggestion));
  const handleClear = () => handleChangeAll(false);
  const handleSelectAll = () => handleChangeAll(true);
  const handleApply = () => {
    setSearchQuery('');
    return onSelect(allSuggestions.filter(({
      selected
    }) => selected).map(suggestionToEventTypeMembership));
  };
  const handleClose = () => {
    setSearchQuery('');
    return onClose();
  };

  // Dark magic 🧙:
  // This prevents dropdown from closing when a user presses Esc, Enter (or other control key) in the search input
  // Also, for Enter it prevents it from bubbling up and submit the form
  const stopEventBubbling = event => {
    if (isEscapeKey(event)) {
      return;
    }
    if (isEnterKey(event)) {
      event.preventDefault();
    }
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };
  const showControls = isMultiSelect && !isFetching && currentSuggestions?.length !== 0 && allSuggestions.length !== 0;
  return <StyledPopupDropdownMenu isMultiSelect={isMultiSelect} isAddingRotatingHosts={isAddingRotatingHosts} {...rest}>
      <MenuContent ref={menuRef} data-component="host-picker">
        <Controls onKeyDown={stopEventBubbling} onKeyPress={stopEventBubbling} onKeyUp={stopEventBubbling}>
          <InlineSearch onChange={value => setSearchQuery(value)} value={searchQuery} placeholder={placeholder} name="host_search" disabled={isDisabled} autoComplete="off" autoFocus />
          {showControls && <FilterContainer>
              <FilterSelectOrClear onClear={handleClear} onSelectAll={handleSelectAll} />
            </FilterContainer>}
        </Controls>
        <div data-container="suggestions" data-component="host-picker-suggestions">
          <SearchContent isMultiSelect={isMultiSelect} onSelect={isMultiSelect ? handleMultiSelection : handleSingleSelection} searching={isFetching} suggestions={currentSuggestions} allSuggestions={allSuggestions} />
        </div>
        {showControls && <Footer>
            <Button size={isPhone ? 'normal' : 'small'} onClick={handleApply}>
              {I18n.t('publisher.action.apply')}
            </Button>
            {!isPhone && <Button size="small" decoration="ghost" onClick={handleClose}>
                {I18n.t('publisher.action.cancel')}
              </Button>}
          </Footer>}
      </MenuContent>
    </StyledPopupDropdownMenu>;
};

require("./host_picker.wyw-in-js.css!=!../../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./host_picker.js");